import {
  getAgentDefaultRoles,
  getAgentModelRoles,
  getModelById,
  getModels,
} from './repository';

function uniqueStrings(values = []) {
  const seen = new Set();
  const result = [];

  for (const value of Array.isArray(values) ? values : []) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    result.push(trimmed);
  }

  return result;
}

function normalizeOverrideValue(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function humanizeAgentRole(role) {
  return String(role || '')
    .replace(/^3d/, '3D')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replace(/\b3d\b/i, '3D');
}

export function getKnownAgentRoles() {
  return uniqueStrings(getModels().flatMap((model) => getAgentModelRoles(model))).sort();
}

export function getRegistryAgentDefaultModelId(role) {
  const normalizedRole = normalizeOverrideValue(role);
  if (!normalizedRole) return null;

  return getModels().find((model) => (
    getAgentDefaultRoles(model).includes(normalizedRole)
  ))?.id || null;
}

export function normalizeAgentModelDefaultOverrides(overrides = {}) {
  const input = overrides && typeof overrides === 'object' && !Array.isArray(overrides)
    ? overrides
    : {};
  const knownRoles = new Set(getKnownAgentRoles());
  const normalized = {};

  for (const [rawRole, rawModelId] of Object.entries(input)) {
    const role = normalizeOverrideValue(rawRole);
    const modelId = normalizeOverrideValue(rawModelId);
    if (!role || !modelId || !knownRoles.has(role)) continue;

    // ⛔ A model that no longer carries the role is KEPT, exactly as `sanitize`
    // keeps it. This runs on every Settings WRITE (`getNextAgentModelDefaultOverrides`
    // normalizes the whole map), so dropping it here meant a preserved choice
    // survived load and then died the next time the user changed an UNRELATED row.
    // Half-dooring the rule that way is worse than not dooring it: the value looks
    // safe until something incidental destroys it.
    //
    // An unknown MODEL is kept for the same reason it is kept on load -- it may be
    // deactivated or briefly absent, and the choice should survive that.
    // Keep a model that EXISTS but no longer carries this role -- the case
    // `sanitize` preserves on load. Dropping it here meant the choice survived a
    // reload and then died the next time the user changed an unrelated row, which
    // is worse than never preserving it: it looks safe until something incidental
    // destroys it.
    //
    // An id resolving to no model at all is still dropped. That is pre-existing,
    // pinned by `agentModelCatalog.test.js`, and deliberately not widened here --
    // `sanitize` keeps such an id on load, so the asymmetry remains and is
    // recorded in the PR rather than changed silently.
    // An UNKNOWN model is kept too, matching `sanitize` on the load path. Dropping
    // it here was the last half-door: the value survived a reload and then died on
    // the next unrelated Settings write, and the lane then auto-picked instead of
    // refusing a broken choice. Deferring this once was wrong -- an asymmetry
    // between the load door and the write door IS the defect, not a smaller
    // version of it.
    const model = getModelById(modelId);
    if (!model) {
      normalized[role] = modelId;
      continue;
    }

    if (getRegistryAgentDefaultModelId(role) === modelId) continue;
    normalized[role] = modelId;
  }

  return normalized;
}

export function sanitizeAgentModelDefaultOverrides(overrides = {}) {
  const input = overrides && typeof overrides === 'object' && !Array.isArray(overrides)
    ? overrides
    : {};
  const knownRoles = new Set(getKnownAgentRoles());
  const sanitized = {};

  for (const [rawRole, rawModelId] of Object.entries(input)) {
    const role = normalizeOverrideValue(rawRole);
    const modelId = normalizeOverrideValue(rawModelId);
    if (!role || !modelId) continue;
    // An unknown MODEL is kept on purpose: it may be deactivated or briefly absent
    // and the user's choice should survive that. An unknown ROLE is different --
    // nothing can ever read it again, so keeping it strands a saved value that
    // raises a permanent "unknown agent role" warning with no Settings row left to
    // clear it from. Retiring a role therefore migrates its saved value out here.
    if (!knownRoles.has(role)) continue;
    // ⛔ A model that no longer carries the role is KEPT here too, and dropping it
    // was a P1: this runs inside `normalizeSettings`, so the saved choice vanished
    // on load and was written back to storage on the next unrelated setting change.
    // The lane then picked by kind and billed a model the user never chose -- the
    // exact substitution INV-MODEL-001 forbids, moved from the lane to the loader.
    // It also silenced the three notices that already handle this: the row renders
    // the stale value as `Unavailable`, the Validation panel names the model and
    // the row, and the agent's refusal says where to fix it. All three read this
    // value. Refusing a de-exposed choice is the contract; deleting it is not.
    if (getRegistryAgentDefaultModelId(role) === modelId) continue;
    sanitized[role] = modelId;
  }

  return sanitized;
}

export function getNextAgentModelDefaultOverrides(overrides, role, modelId) {
  const normalizedRole = normalizeOverrideValue(role);
  const normalizedModelId = normalizeOverrideValue(modelId);
  const next = normalizeAgentModelDefaultOverrides(overrides);

  if (!normalizedRole) return next;

  if (!normalizedModelId) {
    delete next[normalizedRole];
    return next;
  }

  return normalizeAgentModelDefaultOverrides({
    ...next,
    [normalizedRole]: normalizedModelId,
  });
}

export function getAgentDefaultOverrideDiagnostics(overrides = {}) {
  const input = overrides && typeof overrides === 'object' && !Array.isArray(overrides)
    ? overrides
    : {};
  const knownRoles = new Set(getKnownAgentRoles());
  const diagnostics = [];

  for (const [rawRole, rawModelId] of Object.entries(input)) {
    const role = normalizeOverrideValue(rawRole);
    const modelId = normalizeOverrideValue(rawModelId);

    if (!role || !knownRoles.has(role)) {
      diagnostics.push({
        code: 'unknown-role',
        role: role || String(rawRole),
        modelId,
      });
      continue;
    }

    const model = getModelById(modelId);
    if (!model) {
      diagnostics.push({
        code: 'unknown-model',
        role,
        modelId,
      });
      continue;
    }

    if (!getAgentModelRoles(model).includes(role) || model.agent?.available !== true) {
      diagnostics.push({
        code: 'not-agent-exposed',
        role,
        modelId,
        modelLabel: model.label,
      });
    }
  }

  return diagnostics;
}

export function getEffectiveAgentDefaultRoles(model, { agentDefaultOverrides = null } = {}) {
  const registryDefaultRoles = getAgentDefaultRoles(model);
  const normalizedOverrides = normalizeAgentModelDefaultOverrides(agentDefaultOverrides);
  const overriddenRoles = new Set(Object.keys(normalizedOverrides));
  const overrideRolesForModel = Object.entries(normalizedOverrides)
    .filter(([, modelId]) => modelId === model.id)
    .map(([role]) => role);

  return uniqueStrings([
    ...registryDefaultRoles.filter((role) => !overriddenRoles.has(role)),
    ...overrideRolesForModel,
  ]);
}
